import React, { useEffect, useRef, useState } from 'react';
import { MEEM_TOTAL_MAX } from '../../lib/meemItems';
import { RULER_GROUPS, cellState, groupPartial, itemKey } from '../../lib/guidedFlow';

/**
 * Régua de 30: uma célula por item, agrupadas pelos DOMÍNIOS reais da calculadora
 * (orientação temporal 5 · espacial 5 · registro 3 · atenção e cálculo 5 · evocação 3 · linguagem 8 · visuoconstrução 1),
 * com 4px entre grupos e rótulo abreviado sob cada um.
 *
 *   acertou = preenchida (acento) · errou = hachura diagonal (terracota) · não avaliado = contorno tracejado
 *
 * Hover, toque ou clique num grupo troca o rótulo pelo parcial ("7/10") e rola até o primeiro bloco do grupo.
 * É um atalho de ponteiro: as células ficam aria-hidden (o equivalente são os próprios blocos da página, que
 * trazem "n/max"), então o escore em texto é o que tem nome acessível.
 *
 * `cut` = { value, label } marca o ponto de referência vigente (mediana do grupo ou divisão de rastreio).
 */
function RulerGroups({ checked, onGroup, cut, cellClass, showLabels, marked = true }) {
  // `marked=false`: cópia visual (painel do desktop) sem os data-* que testes e atalhos usam para achar a régua principal
  const [active, setActive] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const touch = (group) => {
    setActive(group.id);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setActive(null), 1800);
    if (onGroup) onGroup(group);
  };

  let index = 0;
  return (
    <div className="flex w-full gap-1" aria-hidden="true" data-ruler={marked ? '' : undefined}>
      {RULER_GROUPS.map((group) => {
        const { hits, max } = groupPartial(checked, group);
        const cells = group.blocks.flatMap((b) => b.items.map((_, i) => ({ block: b, i })));
        return (
          <div
            key={group.id}
            className="flex min-w-0 flex-col items-stretch"
            style={{ flex: `${group.max} 1 0` }}
            data-group={marked ? group.id : undefined}
            onPointerEnter={() => setActive(group.id)}
            onPointerLeave={() => setActive(null)}
            onClick={onGroup ? () => touch(group) : undefined}
          >
            <div className={`ruler-group ${cellClass}`} style={onGroup ? { cursor: 'pointer' } : undefined}>
              {cells.map(({ block, i }) => {
                const key = itemKey(block.id, i);
                const position = index;
                index += 1;
                const whole = cut ? Math.floor(cut.value) : null;
                const frac = cut ? cut.value - whole : 0;
                let markLeft = null;
                if (cut && frac === 0 && position === whole - 1) markLeft = '100%';
                if (cut && frac > 0 && position === whole) markLeft = `${frac * 100}%`;
                return (
                  <div key={key} className="ruler-cell" data-state={cellState(checked, key)} data-cell={marked ? key : undefined}>
                    {markLeft !== null && <span className="ruler-mark" style={{ left: markLeft }} data-cut="" />}
                  </div>
                );
              })}
            </div>
            {showLabels && (
              <span className="mt-1 block whitespace-nowrap text-center text-[12px] leading-none text-[var(--ink-2)] md:text-[length:var(--fs-13)]" data-label>
                {active === group.id ? <span className="mono text-[var(--ink)]">{hits}/{max}</span> : group.abbr}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Régua fixa no topo da calculadora. O invólucro tem altura RESERVADA (sem CLS); ao rolar, a barra visível
 * encolhe para 24px (só as células e o escore) sem empurrar o conteúdo.
 */
export function StickyRuler({ checked, total, onGroup }) {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; setCompact(window.scrollY > 120); });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (frame) cancelAnimationFrame(frame); };
  }, []);

  return (
    <div className="pointer-events-none sticky top-14 z-30 h-12 md:h-14 no-print min-[1180px]:hidden" data-ruler-bar>
      <div
        className={`pointer-events-auto border-b border-[var(--line)] bg-[var(--bg)] transition-[height] duration-[120ms] ease-out ${compact ? 'h-6' : 'h-12 md:h-14'}`}
        data-compact={compact ? '' : undefined}
      >
        <div className="mx-auto flex h-full max-w-[820px] items-center gap-3 px-4">
          <div className={compact ? 'h-3.5 flex-1' : 'flex-1'}>
            <RulerGroups checked={checked} onGroup={onGroup} cellClass="ruler-h-sticky" showLabels={!compact} />
          </div>
          <p className="mono shrink-0 whitespace-nowrap text-[length:var(--fs-15)]" aria-label={`Pontuação parcial: ${total} de ${MEEM_TOTAL_MAX}`}>
            <span className="font-semibold">{total}</span>
            <span className="text-[var(--ink-2)]">/{MEEM_TOTAL_MAX}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Régua do resultado: maior, com rótulos, a marca fina âmbar do ponto de referência e a legenda. */
export function ResultRuler({ checked, cut }) {
  return (
    <div>
      <RulerGroups checked={checked} cut={cut} cellClass="ruler-h-result" showLabels />
      <p className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[length:var(--fs-13)] text-[var(--ink-2)]">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="ruler-cell !h-3 !w-3" data-state="hit" />acertou</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="ruler-cell !h-3 !w-3" data-state="miss" />errou</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="ruler-cell !h-3 !w-3" data-state="na" />não avaliado</span>
        {cut && <span className="inline-flex items-center gap-1.5 text-[var(--cut)]"><span aria-hidden="true" className="inline-block h-3.5 w-[2px] bg-[var(--cut)]" />{cut.label}</span>}
      </p>
    </div>
  );
}

/**
 * Painel de pontuação (desktop, ≥ 1180 px): coluna fixa à direita da calculadora, no lugar da régua do topo.
 * Soma ao vivo, régua de 30, parcial por domínio (cada linha leva ao bloco), itens pendentes e "Ver resultado".
 * É o "mostrador do instrumento": o que muda enquanto você aplica fica sempre à vista, sem ocupar a coluna dos itens.
 */
export function ScorePanel({ checked, total, pendentes, onGroup, onShowResult, children }) {
  return (
    <div className="sticky top-[72px] rounded-[8px] border border-[var(--line)] bg-[var(--surface)] p-5" data-score-panel>
      <p className="text-[length:var(--fs-13)] font-medium text-[var(--ink-2)]">Soma até agora</p>
      <p className="mt-1 flex items-baseline gap-1.5" aria-label={`Soma parcial: ${total} de ${MEEM_TOTAL_MAX}`}>
        <span className="score-figure font-display text-[length:var(--fs-64)] text-[var(--ink)]">{total}</span>
        <span className="text-[length:var(--fs-20)] font-medium text-[var(--ink-2)]">/ {MEEM_TOTAL_MAX}</span>
      </p>
      <div className="mt-5">
        <RulerGroups checked={checked} onGroup={onGroup} cellClass="ruler-h-panel" showLabels={false} marked={false} />
      </div>
      <ul className="mt-4 border-t border-[var(--line)]" aria-label="Pontos por domínio">
        {RULER_GROUPS.map((group) => {
          const { hits, max } = groupPartial(checked, group);
          return (
            <li key={group.id}>
              <button
                type="button"
                onClick={() => onGroup(group)}
                className="flex min-h-9 w-full items-center justify-between gap-3 border-b border-[var(--line)] px-1 text-left text-[length:var(--fs-13)] text-[var(--ink-2)] hover:bg-[var(--hover)]"
              >
                <span>{group.label}</span>
                <span className="tabular font-semibold text-[var(--ink-2)]">{hits}/{max}<span className="sr-only">, ir ao bloco</span></span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 min-h-10 text-[length:var(--fs-13)] leading-snug text-[var(--ink-2)]" aria-hidden="true">
        {pendentes > 0 ? `${pendentes} ${pendentes === 1 ? 'item não avaliado' : 'itens não avaliados'}. Itens em branco contam zero.` : 'Todos os itens avaliados.'}
      </p>
      <button type="button" data-analytics-event="calculator_used" onClick={onShowResult} className="btn-primary mt-2 w-full">Ver o resultado</button>
      <p className="mt-2 text-center text-[length:var(--fs-13)] text-[var(--ink-2)]">Atalho: tecla R</p>
      {children}
    </div>
  );
}
